import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userPreferencesService, UserPreferences } from '@/services/userPreferencesService';
import { useAuth } from '@/contexts/AuthContext';

export const userPreferencesKeys = {
  all: ['userPreferences'] as const,
  // Preferences belong to a user. "Run as user" changes identity in-app, without a reload, so the
  // key carries the identity: the impersonated user's preferences are read, never the cached
  // impersonator's.
  forUser: (userId: string | undefined) => [...userPreferencesKeys.all, userId ?? ''] as const,
};

export function useUserPreferencesQuery() {
  const { user } = useAuth();
  return useQuery({
    queryKey: userPreferencesKeys.forUser(user?.sub),
    queryFn: () => userPreferencesService.get(),
    staleTime: Infinity,
  });
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = userPreferencesKeys.forUser(user?.sub);

  return useMutation({
    mutationFn: (settings: Partial<Record<string, string>>) =>
      userPreferencesService.update(settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<UserPreferences>(queryKey);

      queryClient.setQueryData<UserPreferences>(queryKey, (old) =>
        old ? { ...old, ...settings } as UserPreferences : old,
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
